import { Suspense, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import ScrollMemory from 'react-router-scroll-memory';
import { isEmpty } from 'lodash';
import { isBookingPaid, useMetaPurchaseTracking } from 'utils/tracking';
import RebuildCart from 'components/rebuild_cart';
import { setStep } from 'redux/actions/booking';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import TripAdvisor from 'tenants/cccsyd/trip_advisor_conversion_pixel';
import BookWithConfidence from '../book_with_confidence';
import BookingSteps from '../booking_steps';
import CartSummary from '../cart_summary';
import CheckoutForm from '../checkout_form';
import ConfirmBooking from '../confirm_booking';
import EmptyCart from '../empty_cart';
import RelatedProducts from '../related_products';
import ReviewCart from '../review_cart';
import StepControl from '../step_control';

export const Review = () => {
  const location = useLocation();
  const warningMsg = location.state?.warning;
  const { relatedProductsTitle, relatedProductsText, hideRelatedProducts } =
    window.ecom.craftConfig.relatedProducts;
  const { title, text, link, linkText } = window.ecom.craftConfig.emptyCart;
  const bookingInfo = useSelector(getCurrentBooking);
  const hasCartData = bookingInfo.reservations_attributes?.length;
  return (
    <div data-testid="review-step">
      {hasCartData ? (
        <>
          <div className="row cart-section__header mb-4">
            <div className="col-12 col-lg-8">
              <h2>Review your Cart</h2>
              <div className="mt-2 text-x-small text-body-secondary">
                Your booking reference is: {bookingInfo.reference}
              </div>
            </div>
            <div className="col-12 col-lg-4">
              <BookingSteps />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8 divider-y-6 pb-5">
              <ReviewCart />
              <div className="text-end border-0 py-0 mt-4">
                <Link
                  className="d-block text-decoration-none"
                  to="/cart/checkout"
                  title="Continue to Checkout"
                >
                  Continue to Checkout{' '}
                  <i className="fas fa-arrow-right ms-1" aria-hidden="true" />
                </Link>
              </div>
              {!hideRelatedProducts && (
                <RelatedProducts
                  {...{
                    title: relatedProductsTitle,
                    text: relatedProductsText,
                  }}
                />
              )}
              <StepControl />
            </div>
            <div className="col-lg-4 space-y-4">
              <BookWithConfidence />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="row cart-section__header mb-4">
            <div className="col-12 col-lg-8">
              <h2>Your cart is empty</h2>
              {warningMsg && (
                <div className="mt-4 alert alert-warning" role="alert">
                  {warningMsg}
                </div>
              )}
            </div>
            <div className="col-12 col-lg-4">
              <BookingSteps />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8 divider-y-6 pb-5">
              <EmptyCart {...{ title, text, link, linkText }} />
              <RelatedProducts
                {...{ title: relatedProductsTitle, text: relatedProductsText }}
              />
            </div>
            <div className="col-lg-4 space-y-4">
              <BookWithConfidence />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const Checkout = () => {
  const bookingInfo = useSelector(getCurrentBooking);

  // Same unhydrated-store failure class as /cart/confirm (SBAU-613): the
  // server serves the SPA for /cart/checkout unconditionally, so a bookmarked
  // or expired-session visit renders with an empty booking and CheckoutForm
  // throws deep inside the payment widgets, blanking the page. Send the
  // visitor to the cart instead. `push` keeps the original URL in history so
  // Back and refresh still work after a transient hydration failure.
  if (isEmpty(bookingInfo)) {
    return (
      <Redirect
        push
        to={{
          pathname: '/cart',
          state: {
            warning:
              'We could not find a booking to check out. Your cart may have expired — please start again.',
          },
        }}
      />
    );
  }

  return (
    <div data-testid="checkout-step">
      <div className="row cart-section__header mb-4">
        <div className="col-12 col-lg-8">
          <div className="d-lg-none">
            <Link to="/cart" className="checkout__back">
              <i className="fas fa-chevron-left" /> Return to Cart Review
            </Link>
          </div>
          <h2>Let&rsquo;s finalise your booking</h2>
          <div className="mt-2 text-x-small text-body-secondary">
            Your booking reference is: {bookingInfo.reference}
          </div>
        </div>
        <div className="col-12 col-lg-4">
          <div className="mt-4 d-lg-none">
            <CartSummary />
          </div>
          <BookingSteps />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8 space-y-4">
          <CheckoutForm />
        </div>
        <div className="col-lg-4 space-y-4">
          <div className="d-none d-lg-block">
            <CartSummary />
          </div>
        </div>
      </div>
    </div>
  );
};

export const Confirm = () => {
  const bookingInfo = useSelector(getCurrentBooking);

  // Meta pixel purchase tracking. Gating, ordering and the one-shot mark all
  // live in the shared hook (SBAU-611).
  useMetaPurchaseTracking(bookingInfo);

  // There is nothing to confirm when the store holds no booking, which happens
  // on a fresh load with no session booking, without (or with an invalid)
  // booking_id, or when hydration failed upstream. Rendering anyway would show
  // a receipt addressed to nobody with a blank reference and fire the
  // TripAdvisor conversion pixel for a booking that does not exist. The cart
  // has a proper empty state, and carrying a warning through to it says why -
  // the same move RebuildCart makes from this Switch, and sealink's receipt
  // makes for its own confirmation view (SBAU-613).
  // `push` (not the default replace) keeps /cart/confirm?booking_id=N in
  // history, so Back returns to it and a refresh can retry after a transient
  // hydration failure.
  if (isEmpty(bookingInfo)) {
    return (
      <Redirect
        push
        to={{
          pathname: '/cart',
          state: {
            warning:
              'We could not find that booking. If you have just completed a booking, your confirmation has been emailed to you.',
          },
        }}
      />
    );
  }

  return (
    <>
      {/* Conversion pixel, so gated on paid like the Meta pixel above: the
          confirm route is reachable with a hydrated but unpaid booking
          (bookmarked URL, abandoned payment) and must not report a sale. */}
      {isBookingPaid(bookingInfo) && <TripAdvisor />}
      <div className="row" data-testid="confirm-step">
        <div className="col-lg-8 space-y-4">
          <ConfirmBooking />
          <StepControl />
        </div>
        <div className="col-lg-4 space-y-4">
          <BookingSteps />
          <div className="d-none d-lg-block">
            <CartSummary readOnly paid title="Order Confirmation" />
          </div>
        </div>
      </div>
    </>
  );
};

export function Cart() {
  const stepUrls = ['/cart', '/cart/checkout', '/cart/confirm'];
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setStep(stepUrls.indexOf(window.location.pathname) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Suspense fallback={<Skeleton height={800} />}>
      <ScrollMemory />
      <Switch>
        <Route path="/cart" exact component={Review} />
        <Route path="/cart/checkout" exact component={Checkout} />
        <Route path="/cart/confirm" exact component={Confirm} />
        <Route path="/cart/rebuild" exact component={RebuildCart} />
        <Route path="/cart" render={() => <Redirect to="/cart" />} />
      </Switch>
    </Suspense>
  );
}
